import { useEffect, useRef, useState, useCallback } from "react";
import { signOut } from "../auth";
import { fetchAuthSession } from "aws-amplify/auth";
import { pageRoutes } from "../constants/pageRoutes";

export const useSessionMonitor = () => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const logoutTimeoutRef = useRef();
  const warningTimeoutRef = useRef();
  const currentTokenExpRef = useRef(null); // Track token expiry time

  const clearTimers = () => {
    clearTimeout(logoutTimeoutRef.current);
    clearTimeout(warningTimeoutRef.current);
  };

  const scheduleSessionTimeouts = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session?.tokens?.accessToken;
      const expiresAt = session?.tokens?.idToken?.payload?.exp * 1000;

      if (!accessToken || !expiresAt) return;

      // Only schedule new timers if expiry changed
      if (currentTokenExpRef.current === expiresAt) return;

      currentTokenExpRef.current = expiresAt; // Save latest expiry
      clearTimers();

      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry <= 0) {
        await signOut();
        window.location.href = pageRoutes.login;
        return;
      }

      const warnAt = timeUntilExpiry - 2 * 60 * 1000;

      if (warnAt > 0) {
        warningTimeoutRef.current = setTimeout(() => {
          setShowSessionModal(true);
        }, warnAt);
      } else {
        setShowSessionModal(true); // Already within warning window
      }

      logoutTimeoutRef.current = setTimeout(async () => {
        await signOut();
        window.location.href = pageRoutes.login;
      }, timeUntilExpiry);
    } catch (error) {
      console.error("Failed to schedule session timers", error);
      await signOut();
      window.location.href = pageRoutes.login;
    }
  }, []);

  const handleStaySignedIn = useCallback(async () => {
    setShowSessionModal(false);
    clearTimers();
    await fetchAuthSession({ forceRefresh: true });
    currentTokenExpRef.current = null; // Force reschedule on next tick
    scheduleSessionTimeouts(); // Reschedule after refresh
  }, [scheduleSessionTimeouts]);

  const handleLogout = useCallback(async () => {
    setShowSessionModal(false);
    await signOut();
    window.location.href = pageRoutes.login;
  }, []);

  useEffect(() => {
    // Check token every 60s
    const intervalId = setInterval(() => {
      scheduleSessionTimeouts();
    }, 60 * 1000);

    // Initial check
    scheduleSessionTimeouts();

    return () => {
      clearInterval(intervalId);
      clearTimers();
    };
  }, [scheduleSessionTimeouts]);

  return { showSessionModal, handleStaySignedIn, handleLogout };
};
